<template>
  <div v-if="statistics" class="corrosion-panel">
    <!-- Clip Volume Indicator -->
    <div v-if="clipVolumeActive" class="clip-indicator">
      <v-icon x-small color="#4a9eff">mdi-crop-free</v-icon>
      <span>Crop Box Active</span>
    </div>

    <!-- Health Score -->
    <div class="health-section">
      <div class="section-header">
        <v-icon x-small>mdi-heart-pulse</v-icon>
        <span>Pipeline Health Score</span>
      </div>
      <div class="health-display">
        <div class="health-value" :style="{ color: getHealthScoreColor(statistics.healthScore) }">
          {{ statistics.healthScore.toFixed(1) }}%
        </div>
        <div class="health-label">{{ getHealthScoreLabel(statistics.healthScore) }}</div>
      </div>
      <div class="health-bar">
        <div
          class="health-fill"
          :style="{ width: statistics.healthScore + '%', backgroundColor: getHealthScoreColor(statistics.healthScore) }"
        ></div>
      </div>
    </div>

    <!-- Point Distribution -->
    <div class="distribution-section">
      <div class="section-header">
        <v-icon x-small>mdi-chart-pie</v-icon>
        <span>Point Distribution</span>
      </div>
      <div class="distribution-list">
        <div
          v-for="(cat, idx) in distributionCategories"
          :key="idx"
          class="distribution-item"
        >
          <div class="dist-info">
            <div class="dist-color" :style="{ backgroundColor: cat.color }"></div>
            <span class="dist-name">{{ cat.name }}</span>
            <span class="dist-range">{{ cat.range }}</span>
          </div>
          <div class="dist-stats">
            <span class="dist-count">{{ cat.count }}</span>
            <span class="dist-percent">{{ cat.percentage }}%</span>
          </div>
          <div class="dist-bar">
            <div class="dist-fill" :style="{ width: cat.percentage + '%', backgroundColor: cat.color }"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Distance Metrics -->
    <div class="metrics-section">
      <div class="section-header">
        <v-icon x-small>mdi-ruler</v-icon>
        <span>Distance Metrics</span>
      </div>
      <div class="metrics-list">
        <div class="metric-row">
          <span class="metric-label">Total Points</span>
          <span class="metric-value">{{ statistics.totalPoints.toLocaleString() }}</span>
        </div>
        <div class="metric-row">
          <span class="metric-label">Max Deviation</span>
          <span class="metric-value">{{ (statistics.maxDistance * 1000).toFixed(3) }} mm</span>
        </div>
        <div class="metric-row">
          <span class="metric-label">Expected Radius</span>
          <span class="metric-value">{{ (statistics.cylinderRadius * 1000).toFixed(0) }} mm</span>
        </div>
      </div>
    </div>

    <!-- Color Legend / Threshold Editor -->
    <div class="legend-section">
      <div class="section-header">
        <v-icon x-small>mdi-palette</v-icon>
        <span>{{ showAdvancedRange ? 'Threshold Editor' : 'Color Legend' }}</span>
        <v-spacer></v-spacer>
        <button class="toggle-btn" @click="toggleRangeEditor">
          <v-icon x-small>{{ showAdvancedRange ? 'mdi-eye' : 'mdi-tune' }}</v-icon>
        </button>
      </div>

      <!-- Simple Legend -->
      <div v-if="!showAdvancedRange" class="corrosion-legend-list">
        <div v-for="item in legendItems" :key="item.label" class="legend-item">
          <div class="legend-color" :style="{ backgroundColor: item.color }"></div>
          <div class="legend-info">
            <span class="legend-label">{{ item.label }}</span>
            <span class="legend-range">{{ item.range }}</span>
          </div>
        </div>
      </div>

      <!-- Advanced Editor -->
      <corrosion-color-range-bar
        v-else
        :initial-thresholds="currentThresholds"
        @thresholds-changed="handleThresholdsChanged"
        @preset-applied="handlePresetApplied"
        @reset="handleReset"
        @visibility-changed="handleVisibilityChanged"
      />
    </div>
  </div>
</template>

<script>
import CorrosionColorRangeBar from './CorrosionColorRangeBar.vue'
import { pittingCategory } from '@/features/Dashboard/config/enums'

export default {
  name: 'CorrosionAnalysisPanel',

  components: {
    CorrosionColorRangeBar
  },

  props: {
    statistics: {
      type: Object,
      default: null
    },
    clipVolumeActive: {
      type: Boolean,
      default: false
    },
    initialThresholds: {
      type: Object,
      default: null
    },
    tagId: {
      type: String,
      default: null
    },
    customCategories: {
      type: Array,
      default: null
    }
  },

  data() {
    const defaultThresholds = {
      normalToMinor: 2.0,
      minorToModerate: 4.0,
      moderateToCritical: 6.0,
      maxDisplay: 15.0
    };

    const currentThresholds = this.initialThresholds ? {
      normalToMinor: (this.initialThresholds.normalToMinor || 0.002) * 1000,
      minorToModerate: (this.initialThresholds.minorToModerate || 0.004) * 1000,
      moderateToCritical: (this.initialThresholds.moderateToCritical || 0.006) * 1000,
      maxDisplay: (this.initialThresholds.maxDisplay || 0.015) * 1000
    } : defaultThresholds;

    return {
      showAdvancedRange: false,
      currentThresholds
    }
  },

  computed: {
    categories() {
      return this.customCategories || pittingCategory;
    },

    categoryTooltips() {
      return [
        `${this.categories[0].name} - 0-${this.currentThresholds.normalToMinor}mm`,
        `${this.categories[1].name} - ${this.currentThresholds.normalToMinor}-${this.currentThresholds.minorToModerate}mm`,
        `${this.categories[2].name} - ${this.currentThresholds.minorToModerate}-${this.currentThresholds.moderateToCritical}mm`,
        `${this.categories[3].name} - ${this.currentThresholds.moderateToCritical}mm+`
      ];
    },

    distributionCategories() {
      return [
        {
          name: this.categories[0].description,
          range: `0-${this.currentThresholds.normalToMinor}mm`,
          count: this.statistics.normalPoints.toLocaleString(),
          percentage: this.statistics.normalPercentage,
          color: this.categories[0].color
        },
        {
          name: this.categories[1].description,
          range: `${this.currentThresholds.normalToMinor}-${this.currentThresholds.minorToModerate}mm`,
          count: this.statistics.minorWear.toLocaleString(),
          percentage: this.statistics.minorPercentage,
          color: this.categories[1].color
        },
        {
          name: this.categories[2].description,
          range: `${this.currentThresholds.minorToModerate}-${this.currentThresholds.moderateToCritical}mm`,
          count: this.statistics.moderateCorrosion.toLocaleString(),
          percentage: this.statistics.moderatePercentage,
          color: this.categories[2].color
        },
        {
          name: this.categories[3].description,
          range: `${this.currentThresholds.moderateToCritical}mm+`,
          count: this.statistics.criticalCorrosion.toLocaleString(),
          percentage: this.statistics.criticalPercentage,
          color: this.categories[3].color
        }
      ];
    },

    legendItems() {
      return [
        {
          color: this.categories[0].color,
          label: this.categories[0].description,
          range: `0-${this.currentThresholds.normalToMinor}mm`
        },
        {
          color: this.categories[1].color,
          label: this.categories[1].description,
          range: `${this.currentThresholds.normalToMinor}-${this.currentThresholds.minorToModerate}mm`
        },
        {
          color: this.categories[2].color,
          label: this.categories[2].description,
          range: `${this.currentThresholds.minorToModerate}-${this.currentThresholds.moderateToCritical}mm`
        },
        {
          color: this.categories[3].color,
          label: this.categories[3].description,
          range: `${this.currentThresholds.moderateToCritical}mm+`
        }
      ]
    }
  },

  methods: {
    getHealthScoreColor(score) {
      if (score >= 90) return '#4caf50'
      if (score >= 75) return '#8bc34a'
      if (score >= 50) return '#ffc107'
      if (score >= 25) return '#ff9800'
      return '#f44336'
    },

    getHealthScoreLabel(score) {
      if (score >= 90) return 'Excellent'
      if (score >= 75) return 'Good'
      if (score >= 50) return 'Fair'
      if (score >= 25) return 'Poor'
      return 'Critical'
    },

    toggleRangeEditor() {
      this.showAdvancedRange = !this.showAdvancedRange
    },

    handleThresholdsChanged(thresholdsInMeters) {
      this.currentThresholds = {
        normalToMinor: thresholdsInMeters.normalToMinor * 1000,
        minorToModerate: thresholdsInMeters.minorToModerate * 1000,
        moderateToCritical: thresholdsInMeters.moderateToCritical * 1000,
        maxDisplay: thresholdsInMeters.maxDisplay * 1000
      }
      this.$emit('thresholds-changed', thresholdsInMeters)
    },

    handlePresetApplied(presetName) {
      this.$emit('preset-applied', presetName)
    },

    handleReset() {
      this.$emit('reset-thresholds')
    },

    handleVisibilityChanged(visibilitySettings) {
      this.$emit('visibility-changed', visibilitySettings)
    },

    handleInspectAction() {
      if (this.tagId) {
        this.$emit('update-inspect', { tagId: this.tagId });
      } else {
        this.$emit('create-inspect');
      }
    }
  }
}
</script>

<style scoped>
/* ============================================
   CORROSION ANALYSIS PANEL - Professional CAD Style
   ============================================ */

.corrosion-panel {
  --bg-panel: #2b2b2b;
  --bg-elevated: #323232;
  --bg-card: #363636;
  --border-color: #4a4a4a;
  --border-subtle: #3d3d3d;
  --text-primary: #e0e0e0;
  --text-secondary: #a0a0a0;
  --text-muted: #707070;
  /* Green accent - Unified with toolbar (#03624c family) */
  --accent-primary: #03624c;
  --accent-primary-light: #a8c97a;
  --accent-blue: #4a9eff;
  --radius: 4px;
  --transition: 150ms ease;

  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 12px;
  color: var(--text-primary);
}

/* Clip Indicator */
.clip-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: rgba(74, 158, 255, 0.1);
  border-left: 2px solid var(--accent-blue);
  border-radius: 0 var(--radius) var(--radius) 0;
  margin-bottom: 12px;
  font-size: 11px;
  color: var(--accent-blue);
}

/* Section Headers */
.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-header .v-icon {
  color: var(--accent-primary-light) !important;
  font-size: 16px !important;
  opacity: 0.9;
}

/* Health Section */
.health-section {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  padding: 12px;
  margin-bottom: 12px;
}

.health-display {
  text-align: center;
  margin: 12px 0;
}

.health-value {
  font-size: 36px;
  font-weight: 700;
  line-height: 1;
}

.health-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-top: 4px;
}

.health-bar {
  height: 6px;
  background: var(--bg-elevated);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 12px;
}

.health-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.4s ease;
}

/* Distribution Section */
.distribution-section {
  margin-bottom: 12px;
}

.distribution-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.distribution-item {
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius);
  padding: 8px 10px;
}

.dist-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.dist-color {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}

.dist-name {
  font-weight: 500;
  color: var(--text-primary);
  flex: 1;
}

.dist-range {
  font-size: 10px;
  font-weight: 500;
  color: var(--text-secondary);
  padding: 3px 8px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  margin-left: auto;
  white-space: nowrap;
}

.dist-stats {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.dist-count {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.dist-percent {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-card);
  padding: 2px 6px;
  border-radius: var(--radius);
}

.dist-bar {
  height: 3px;
  background: var(--bg-panel);
  border-radius: 2px;
  overflow: hidden;
}

.dist-fill {
  height: 100%;
  transition: width 0.3s ease;
}

/* Metrics Section */
.metrics-section {
  margin-bottom: 12px;
}

.metrics-list {
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: var(--border-subtle);
  border-radius: var(--radius);
  overflow: hidden;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: var(--bg-elevated);
}

.metric-label {
  font-size: 11px;
  color: var(--text-secondary);
}

.metric-value {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

/* Legend Section */
.legend-section {
  margin-bottom: 12px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius);
  padding: 10px;
}

.legend-section .section-header {
  margin-bottom: 8px;
}

.toggle-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition);
}

.toggle-btn:hover {
  background: var(--bg-hover, #404040);
  color: var(--accent-primary-light);
  border-color: var(--accent-primary);
}

.toggle-btn .v-icon {
  font-size: 14px !important;
}

.corrosion-legend-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius);
  transition: all var(--transition);
}

.legend-item:hover {
  border-color: var(--border-color);
  background: var(--bg-hover, #404040);
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.legend-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.legend-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-primary);
}

.legend-range {
  font-size: 10px;
  font-weight: 500;
  color: var(--text-muted);
}

/* Actions Section */
.actions-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--border-subtle);
}

.action-btn {
  font-size: 11px !important;
  font-weight: 500 !important;
  letter-spacing: 0.3px !important;
}
</style>
